import React, { useEffect } from "react";
import { Select, Typography, Form, theme, Divider, Input, Radio } from "antd";
import { GET_PAID_STATUS, GET_PAID_STEP, GetPaidData, GetPaidRawFormData } from "types/get_paid";
import { useTranslation } from "react-i18next";
import { useGetPaidMixpanelEvent, useStepValidation } from "hooks/get_paid";
import FileUploader from "../Integrations/FileUploader";
import { prefillGetPaidForm } from "helpers/get_paid";

const OrganizationInfoStep: React.FC<{
  getPaidInitialData?: GetPaidData | null;
  getPaidStatus?: GET_PAID_STATUS;
  handleShouldGoNext: (shouldGoNext: boolean) => void;
}> = ({ getPaidInitialData, getPaidStatus, handleShouldGoNext }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const form = Form.useFormInstance<GetPaidRawFormData>();

  const hasWebsite = Form.useWatch([GET_PAID_STEP.ORGANIZATION_INFO, "has_website"], form);
  const organizationType = Form.useWatch(
    [GET_PAID_STEP.ORGANIZATION_INFO, "organization_type"],
    form
  );

  const uploadFields = ["national_address", "commercial_document", "owner_id", "iban_letter"] as const;

  useGetPaidMixpanelEvent({
    eventName: "AR_GP_Process_Start",
    fieldGroup: GET_PAID_STEP.ORGANIZATION_INFO,
    isAllowed: () => {
      const [organizationIndustry, organizationSize, organizationType] = [
        form.getFieldValue([
          GET_PAID_STEP.ORGANIZATION_INFO,
          "organization_industry",
        ]) as string | undefined,
        form.getFieldValue([
          GET_PAID_STEP.ORGANIZATION_INFO,
          "organization_size",
        ]) as string | undefined,
        form.getFieldValue([
          GET_PAID_STEP.ORGANIZATION_INFO,
          "organization_type",
        ]) as string | undefined,
      ];
      if (
        organizationIndustry !==
          getPaidInitialData?.organization_industry?.defaultValue ||
        organizationSize !== getPaidInitialData?.organization_size?.defaultValue ||
        Boolean(organizationType)
      ) {
        return true;
      }

      return false;
    },
  });

  useEffect(() => {
    const [organizationIndustry, organizationSize] = [
      form.getFieldValue([
        GET_PAID_STEP.ORGANIZATION_INFO,
        "organization_industry",
      ]) as string | undefined,
      form.getFieldValue([
        GET_PAID_STEP.ORGANIZATION_INFO,
        "organization_size",
      ]) as string | undefined,
    ];
    if (getPaidInitialData && !organizationIndustry && !organizationSize) {
      prefillGetPaidForm(form, GET_PAID_STEP.ORGANIZATION_INFO, [
        "organization_industry",
        "organization_size",
      ], getPaidInitialData);
    }
  }, [getPaidInitialData]);

  useEffect(() => {
    if (getPaidInitialData && getPaidStatus === GET_PAID_STATUS.REJECTED) {
      prefillGetPaidForm(
        form,
        GET_PAID_STEP.ORGANIZATION_INFO,
        [...uploadFields, "organization_type", "has_website"],
        getPaidInitialData
      );
      if (getPaidInitialData?.has_website.defaultValue === "yes") {
        prefillGetPaidForm(form, GET_PAID_STEP.ORGANIZATION_INFO, ["website"], getPaidInitialData);
      }
      if (getPaidInitialData?.has_website.defaultValue === "no") {
        prefillGetPaidForm(form, GET_PAID_STEP.ORGANIZATION_INFO, ["price_list"], getPaidInitialData);
      }
      if (getPaidInitialData?.tax_register?.defaultValue) {
        prefillGetPaidForm(form, GET_PAID_STEP.ORGANIZATION_INFO, ["tax_register"], getPaidInitialData);
      }
    }
  }, [getPaidStatus]);

  useStepValidation({ handleShouldGoNext, getPaidStatus });

  return (
    <>
      <Typography.Title
        level={4}
        style={{
          marginBottom: token.marginLG,
          fontWeight: token.fontWeightStrong,
        }}
      >
        {t(
          "templates.integrations.getpaid_activation.organization_info.header"
        )}
      </Typography.Title>
      <Divider />
      <Form.Item
        name={[GET_PAID_STEP.ORGANIZATION_INFO, "organization_industry"]}
        label={t(
          "templates.integrations.getpaid_activation.organization_info.industry"
        )}
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          size="large"
          options={getPaidInitialData?.organization_industry?.values}
        />
      </Form.Item>
      <Form.Item
        name={[GET_PAID_STEP.ORGANIZATION_INFO, "organization_size"]}
        label={t(
          "templates.integrations.getpaid_activation.organization_info.size"
        )}
        rules={[{ required: true }]}
      >
        <Select
          size="large"
          options={getPaidInitialData?.organization_size?.values}
        />
      </Form.Item>
      <FileUploader
        name={[GET_PAID_STEP.ORGANIZATION_INFO, "national_address"]}
        label={t("templates.integrations.getpaid_activation.organization_info.national_address")}
        required={true}
        allowPDF={true}
      />
      <Form.Item
        name={[GET_PAID_STEP.ORGANIZATION_INFO, "has_website"]}
        label={t("templates.integrations.getpaid_activation.organization_info.has_website.label")}
        rules={[{ required: true }]}
        labelCol={{ span: "auto" }}
        wrapperCol={{ span: "auto" }}
      >
        <Radio.Group
          options={[
            { 
              value: "yes",
              label: t("templates.integrations.getpaid_activation.organization_info.has_website.yes")
            },
            { 
              value: "no",
              label: t("templates.integrations.getpaid_activation.organization_info.has_website.no")
            },
          ]}
        />
      </Form.Item>
      {hasWebsite === "yes" && 
        <Form.Item
          name={[GET_PAID_STEP.ORGANIZATION_INFO, "website"]}
          label={t("templates.integrations.getpaid_activation.organization_info.website")}
          rules={[{ required: true }]}
        >
          <Input
            size="large"
            placeholder={t("templates.integrations.getpaid_activation.organization_info.website_placeholder")}
          />
        </Form.Item>
      }
      {hasWebsite === "no" &&
        <FileUploader
          name={[GET_PAID_STEP.ORGANIZATION_INFO, "price_list"]}
          label={t("templates.integrations.getpaid_activation.organization_info.price_list")}
          note={t("templates.integrations.getpaid_activation.organization_info.price_list_note")}
          required={true}
          allowCSV={true}
        />
      }
      <Form.Item
        name={[GET_PAID_STEP.ORGANIZATION_INFO, "organization_type"]}
        label={t(
          "templates.integrations.getpaid_activation.organization_info.type"
        )}
        rules={[{ required: true }]}
      >
        <Select
          size="large"
          options={getPaidInitialData?.organization_type?.values}
        />
      </Form.Item>
      <FileUploader
        name={[GET_PAID_STEP.ORGANIZATION_INFO, "commercial_document"]}
        label={t(
          "templates.integrations.getpaid_activation.organization_info.commercial_registration"
        )}
        note={t(
          "templates.integrations.getpaid_activation.organization_info.commercial_registration_note"
        )}
        required={true}
        allowPDF={true}
        disabled={!organizationType}
      />
      <FileUploader
        name={[GET_PAID_STEP.ORGANIZATION_INFO, "owner_id"]}
        label={t(
          "templates.integrations.getpaid_activation.organization_info.owner_id"
        )}
        note={t(
          "templates.integrations.getpaid_activation.organization_info.owner_id_note"
        )}
        required={true}
        allowPDF={true}
        disabled={!organizationType}
      />
      <FileUploader
        name={[GET_PAID_STEP.ORGANIZATION_INFO, "iban_letter"]}
        label={t(
          "templates.integrations.getpaid_activation.organization_info.iban_letter"
        )}
        note={t(
          "templates.integrations.getpaid_activation.organization_info.iban_letter_note"
        )}
        required={true}
        allowPDF={true}
        disabled={!organizationType}
      />
      <FileUploader
        name={[GET_PAID_STEP.ORGANIZATION_INFO, "tax_register"]}
        label={t(
          "templates.integrations.getpaid_activation.organization_info.tax_certificate"
        )}
        allowPDF={true}
        disabled={!organizationType}
      />
    </>
  );
};

export default OrganizationInfoStep;
